import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Book,
  Search,
  Download,
  Loader2,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Help Centre — markdown article library + branded PDF downloads.
 *
 * Three states it renders:
 *   1. Loading skeleton.
 *   2. List + search + role filter (left rail) with empty centre.
 *   3. Article detail view (centre).
 *
 * The `?slug=...` URL query parameter deep-links into an article so the
 * contextual `?`-tooltips elsewhere in the app can jump straight to the
 * right page.
 */
const ROLE_LABEL = {
  viewer: "Viewer",
  contributor: "Contributor",
  manager: "Manager",
  executive: "Executive",
  cpo: "CPO",
  cro: "CRO",
  org_admin: "Org Admin",
  admin: "Platform Admin",
  owner: "Owner",
};

export default function Help() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeSlug, setActiveSlug] = useState(params.get("slug") || null);
  const [active, setActive] = useState(null);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState(
    params.get("role") || (user?.role || ""),
  );
  const [downloadingBundle, setDownloadingBundle] = useState(false);

  // Load list (re-runs when the role filter changes).
  useEffect(() => {
    setLoadingList(true);
    const q = roleFilter ? `?role=${encodeURIComponent(roleFilter)}` : "";
    api
      .get(`/help/articles${q}`)
      .then(({ data }) => setArticles(data.articles))
      .catch(() => toast.error("Failed to load help library"))
      .finally(() => setLoadingList(false));
  }, [roleFilter]);

  // Load active article when slug changes.
  useEffect(() => {
    if (!activeSlug) {
      setActive(null);
      return;
    }
    setLoadingArticle(true);
    api
      .get(`/help/articles/${activeSlug}`)
      .then(({ data }) => setActive(data))
      .catch((e) => {
        toast.error(
          formatApiErrorDetail(e?.response?.data?.detail) ||
            "Article not found",
        );
        setActiveSlug(null);
      })
      .finally(() => setLoadingArticle(false));
  }, [activeSlug]);

  // Keep URL in sync with the active article.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (activeSlug) next.set("slug", activeSlug);
    else next.delete("slug");
    if (roleFilter) next.set("role", roleFilter);
    else next.delete("role");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlug, roleFilter]);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? articles.filter(
          (a) =>
            a.title.toLowerCase().includes(q) ||
            (a.summary || "").toLowerCase().includes(q) ||
            (a.tags || []).some((t) => t.toLowerCase().includes(q)),
        )
      : articles;
    const out = {};
    filtered.forEach((a) => {
      (out[a.category] = out[a.category] || []).push(a);
    });
    return out;
  }, [articles, search]);

  const downloadArticlePdf = async () => {
    if (!active) return;
    try {
      const res = await api.get(`/help/articles/${active.slug}.pdf`, {
        responseType: "blob",
      });
      triggerDownload(res.data, `erio-${active.slug}.pdf`);
    } catch {
      toast.error("PDF download failed");
    }
  };

  const downloadRoleBundle = async () => {
    const role = roleFilter || user?.role;
    if (!role) {
      toast.error("Pick a role first");
      return;
    }
    setDownloadingBundle(true);
    try {
      const res = await api.get(`/help/bundles/${role}.pdf`, {
        responseType: "blob",
      });
      triggerDownload(res.data, `erio-manual-${role}.pdf`);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Bundle download failed",
      );
    } finally {
      setDownloadingBundle(false);
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Help Centre · ERIO" />
      <Navbar />
      <main
        className="mx-auto max-w-6xl px-6 py-10"
        data-testid="help-centre-page"
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="ek-eyebrow">User manual</p>
            <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Help Centre</h1>
            <p className="mt-3 max-w-2xl text-base text-ek-ink/65">
              Searchable, role-based walkthroughs. Every article is also
              available as a branded PDF for offline reading or sharing.
            </p>
          </div>
          <button
            type="button"
            onClick={downloadRoleBundle}
            disabled={downloadingBundle}
            className="inline-flex items-center gap-1.5 rounded-md border border-ek-purple bg-white px-3 py-2 text-sm font-medium text-ek-purple hover:bg-ek-purple/5 disabled:opacity-50"
            data-testid="help-download-bundle"
          >
            {downloadingBundle ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            Download role manual (PDF)
          </button>
        </div>

        <div className="mt-8 grid grid-cols-12 gap-6">
          {/* Sidebar: search + role filter + article list */}
          <aside
            className="col-span-12 md:col-span-4 lg:col-span-3"
            data-testid="help-sidebar"
          >
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2.5 top-2.5 text-ek-ink/40"
                />
                <input
                  type="search"
                  placeholder="Search articles…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="help-search"
                  className="w-full rounded-md border border-slate-300 bg-white py-1.5 pl-8 pr-2 text-sm focus:border-ek-purple focus:outline-none"
                />
              </div>

              <label
                htmlFor="help-role-filter"
                className="mt-3 block text-[11px] font-medium uppercase tracking-wide text-ek-ink/60"
              >
                Filter by role
              </label>
              <select
                id="help-role-filter"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                data-testid="help-role-filter"
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
              >
                <option value="">All roles</option>
                {Object.entries(ROLE_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>

              <div className="mt-4">
                {loadingList ? (
                  <Loader2
                    size={16}
                    className="mx-auto my-6 animate-spin text-ek-purple"
                  />
                ) : Object.keys(groups).length === 0 ? (
                  <p className="my-6 text-center text-xs text-ek-ink/50">
                    No articles match your filter.
                  </p>
                ) : (
                  Object.entries(groups).map(([cat, list]) => (
                    <div key={cat} className="mt-3">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-ek-purple">
                        {cat}
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {list.map((a) => (
                          <li key={a.slug}>
                            <button
                              type="button"
                              onClick={() => setActiveSlug(a.slug)}
                              data-testid={`help-article-link-${a.slug}`}
                              className={`flex w-full items-center justify-between rounded-md px-2 py-1 text-left text-xs hover:bg-ek-purple/5 ${
                                activeSlug === a.slug
                                  ? "bg-ek-purple/10 text-ek-purple"
                                  : "text-ek-ink"
                              }`}
                            >
                              <span className="truncate">{a.title}</span>
                              <ChevronRight
                                size={12}
                                className="shrink-0 opacity-40"
                              />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                )}
              </div>
            </div>
          </aside>

          {/* Article body */}
          <section
            className="col-span-12 md:col-span-8 lg:col-span-9"
            data-testid="help-article-pane"
          >
            <div className="rounded-2xl border border-slate-200 bg-white p-7 min-h-[480px]">
              {loadingArticle ? (
                <Loader2 className="mx-auto mt-20 animate-spin text-ek-purple" />
              ) : !active ? (
                <EmptyState />
              ) : (
                <article>
                  <button
                    type="button"
                    onClick={() => setActiveSlug(null)}
                    className="mb-4 inline-flex items-center gap-1 text-[11px] text-ek-ink/60 hover:text-ek-purple md:hidden"
                  >
                    <ArrowLeft size={12} /> Back to list
                  </button>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-wider text-ek-purple">
                        {active.category}
                      </p>
                      <h2
                        className="ek-heading mt-1 text-2xl sm:text-3xl"
                        data-testid="help-article-title"
                      >
                        {active.title}
                      </h2>
                      {active.summary && (
                        <p className="mt-2 max-w-2xl text-sm italic text-ek-ink/65">
                          {active.summary}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={downloadArticlePdf}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-ek-ink hover:bg-slate-50"
                      data-testid="help-article-download"
                    >
                      <Download size={12} /> PDF
                    </button>
                  </div>

                  <div
                    className="erio-prose mt-5"
                    data-testid="help-article-body"
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {active.body_md}
                    </ReactMarkdown>
                  </div>

                  {active.tags?.length > 0 && (
                    <div className="mt-8 flex flex-wrap gap-1.5">
                      {active.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-ek-ink/65"
                        >
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* "Suggest an edit" — turns docs into a co-owned asset.
                     Mailto with a pre-filled subject + body referencing the
                     slug so the editor knows exactly which article to update. */}
                  <div
                    className="mt-6 border-t border-slate-200 pt-4"
                    data-testid="help-suggest-edit"
                  >
                    <a
                      href={`mailto:info@ekselens.com?subject=${encodeURIComponent(
                        `Help Centre · suggested edit · ${active.title}`,
                      )}&body=${encodeURIComponent(
                        `Article: ${active.title}\nSlug: ${active.slug}\n\nSuggested change / clarification / correction:\n\n`,
                      )}`}
                      className="inline-flex items-center gap-1.5 text-[11.5px] text-ek-ink/65 hover:text-ek-purple"
                      data-testid="help-suggest-edit-link"
                    >
                      <span aria-hidden>✎</span>
                      <span>Suggest an edit to this article</span>
                    </a>
                    <p className="mt-1 text-[10.5px] text-ek-ink/45">
                      Opens an email to <code>info@ekselens.com</code> with
                      the article reference pre-filled.
                    </p>
                  </div>
                </article>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-16 text-center text-ek-ink/55">
    <Book size={32} className="mb-3 text-ek-purple/40" />
    <p className="font-medium text-ek-ink">Pick an article from the left</p>
    <p className="mt-1 text-sm text-ek-ink/50 max-w-md">
      Or use the search box to find a specific topic. Each article covers
      one user workflow, and is also downloadable as a branded PDF.
    </p>
  </div>
);

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
